import React, { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import styled, { css } from 'styled-components';
import Button from 'up-ops-web-shared-components/lib/components/button';
import Link from 'up-ops-web-shared-components/lib/components/link';
import Text from 'up-ops-web-shared-components/lib/components/text';

import successSrc from 'assets/images/success.svg';
import {
  showSubmitSuccessState,
  submissionResultState,
} from 'state/atoms/submit';
import useSubmitActions from 'hooks/submit';
import { trackEvent } from 'lib/tracking';
import { SectionHeader, UnorderedList } from 'styles/CommonStyles';
import { submissionTimestamp } from 'state/selectors/submit';

const contentSectionStyle = css`
  margin-bottom: 4rem;
`;

const StyledHeaderContent = styled.div`
  ${contentSectionStyle}

  h2 {
    color: ${(props) => props.theme.colors.textDefault};
    font-size: 1.25rem;
    margin: 0 0 0.5rem;
  }

  img {
    margin-bottom: 1.5rem;
  }

  .confirmationNumber {
    color: ${(props) => props.theme.colors.primaryAccent};
    font-size: 2.25rem;
    font-weight: 500;
    letter-spacing: 1px;
    line-height: 1;
  }
`;

const StyledMainContent = styled.div`
  ${contentSectionStyle}

  ${SectionHeader} {
    margin-bottom: 0.5rem;
  }
`;

const formTranslationKeys: Record<string, string> = {
  bankruptcy: 'forms.bankruptcy.name',
  card_fraud: 'forms.cardTheft.name',
  deceased_borrower: 'forms.deceased.name',
  identity_theft: 'forms.identityTheft.name',
  request_to_submit_identity: 'forms.identityVerification.name',
};

const getFormTranslationKey = (formIdentifier: string = ''): string => {
  return formTranslationKeys[formIdentifier] || '';
};

const SuccessView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setShowSuccess = useSetRecoilState(showSubmitSuccessState);
  const submissionResult = useRecoilValue(submissionResultState);
  const submissionResultTimestamp = useRecoilValue(submissionTimestamp);
  const { reset } = useSubmitActions();

  const handleAnotherRequest = useCallback(() => {
    setShowSuccess(false);
    reset();
    navigate('/');
    trackEvent('user', 'click', 'restart-after-success');
  }, [navigate, reset, setShowSuccess]);

  const userEmail = submissionResult?.email;

  return (
    <div>
      <StyledHeaderContent>
        <img alt={t('common.success')} src={successSrc} />
        <h2>{t('success.confirmationNumber')}</h2>
        <div className="confirmationNumber">
          {submissionResult?.form_session}
        </div>
      </StyledHeaderContent>

      <StyledMainContent>
        <SectionHeader>{t('success.title')}</SectionHeader>
        <Trans i18nKey="success.nextSteps" values={{ email: userEmail }}>
          We have emailed a confirmation and a copy of your form to
          <Link href={`mailto:${userEmail}`}>{userEmail}</Link>. We will reach
          out if we have any questions or need additional information.
        </Trans>
      </StyledMainContent>

      <UnorderedList>
        <li>
          <Text>
            {t('success.detailsName', { name: submissionResult?.name })}
          </Text>
        </li>
        <li>
          <Text>
            {t('success.detailsFormType', {
              type: t(getFormTranslationKey(submissionResult?.form_type)),
            })}
          </Text>
        </li>
        <li>
          <Text>
            {t('success.detailsTimestamp', {
              timestamp: submissionResultTimestamp,
            })}
          </Text>
        </li>
      </UnorderedList>

      <Button
        inline
        onClick={handleAnotherRequest}
        primary
        style={{ marginTop: '5rem', textTransform: 'uppercase' }}
      >
        {t('success.submitAnotherRequest')}
      </Button>
    </div>
  );
};

export default SuccessView;
